import * as React from 'react';
import Flex from 'ui/Flex/Flex';
import { useGetAvailableFilters } from 'routes/Explore/components/Sentence/hooks/available';
import { List, ListItem } from '@mui/material';
import { useEffect, useState } from 'react';
import { DashboardChartT } from 'business/definitions';
import Button from '@mui/material/Button';
import { Add } from '@mui/icons-material';
import { useDebounce } from 'hooks/helpers';
import categories from 'routes/Explore/components/Sentence/components/functions_categories.json';
import Tree from 'routes/Explore/components/Sentence/components/Tree';
import { useGetDataInTreeFormat } from 'routes/Explore/components/Sentence/hooks/treeData';
import { TreeValue } from 'routes/Explore/components/Sentence/contexts/TreeValueContext';
import { useGetChartMetadata } from 'routes/Explore/hooks/chartMetadata';
import { useTranslation } from 'react-i18next';
import { AlertFilter } from 'lib/alert/models';
import { useSelector } from 'store/hooks';
import CircularProgress from '@mui/material/CircularProgress';
import { MANTY_COLORS } from 'business/constants';
import isEmpty from 'lodash/isEmpty';
import PreviousStepButton from './PreviousStepButton';
import NextStepButton from './NextStepButton';
import { useAlertForm } from '../Contexts/AlertFormContext';
import AlertFilterChunk from './AlertFilterChunk';
import { useAlertSentence } from '../Contexts/AlertSentenceContext';
import { useAlertAction } from '../Contexts/AlertActionContext';
import { isAlertFilterValid } from './validators';
import { useBlockProtection } from './hooks/protections';
import AlertScopeWarning from './AlertScopeWarning';

type Props = {
    chart: DashboardChartT;
};

const AlertPerimeters = ({ chart }: Props): JSX.Element => {
    const { t } = useTranslation();
    const { alert, setAlert } = useAlertForm();
    const { action } = useAlertAction();

    const [showTree, setShowTree] = React.useState(false);
    const [filters, setFilters] = useState<Array<AlertFilter>>(
        alert.filters ?? [],
    );

    const [selectedIndex, setSelectedIndex] = useState(-1);

    const { user, scopes } = useSelector((state) => state.vision);
    const metricProtection = useBlockProtection(alert?.metric?.field);
    const dimensionProtection = useBlockProtection(alert?.dimension?.field);
    const metricScope = scopes.find((s) => metricProtection?.scope === s.name);
    const dimensionScope = scopes.find(
        (s) => dimensionProtection?.scope === s.name,
    );

    const { availableFilters, setAvailableFilters } = useAlertSentence();
    const { data, isLoading, isFetching } = useGetAvailableFilters(
        alert.metric ? [alert.metric] : [],
        !availableFilters,
    );
    const availableFiltersLoading = isLoading || isFetching;

    const {
        data: chartMetadata,
        isLoading: isChartMetadataLoading,
        isFetching: isChartMetadataFetching,
    } = useGetChartMetadata(chart.key, !alert.filters);

    const chartMetadataLoading =
        isChartMetadataLoading || isChartMetadataFetching;

    useEffect(() => {
        if (data && !availableFiltersLoading) {
            const options = data.filter((f) => f.type !== 'table');
            setAvailableFilters(options);
        }
    }, [data, availableFiltersLoading]);

    useEffect(() => {
        if (chartMetadata && !chartMetadataLoading && isEmpty(alert.filters)) {
            setFilters((prevState) => {
                const defaultFiltersWithDisplayIndex =
                    chartMetadata.filters.map((f, index) => ({
                        ...f,
                        displayIndex: index + 1,
                    }));
                return [...prevState, ...defaultFiltersWithDisplayIndex];
            });
        }
    }, [chartMetadata, chartMetadataLoading]);

    const treeData = useGetDataInTreeFormat(availableFilters ?? [], categories);

    const userScopes = user.scopes;

    const onFilterClick = (fieldName: TreeValue) => {
        const newAvailableFilter = availableFilters?.find(
            (element) => element.field === fieldName,
        ) as AvailableElementT;
        const newFilter: AlertFilter = {
            field: newAvailableFilter.field,
            operation: 'equal',
            type: newAvailableFilter.type,
            value: '',
            valueScale: null,
            displayIndex: filters.length + 1,
        };

        setFilters((prevState) => [...prevState, newFilter]);
        setShowTree(false);
    };

    const removeChunk = (index: number): void => {
        setFilters((prevState) =>
            prevState
                .filter((f) => f.displayIndex !== index)
                .map((f) => ({
                    ...f,
                    displayIndex:
                        f.displayIndex > index
                            ? f.displayIndex - 1
                            : f.displayIndex,
                })),
        );
    };

    const changeSelectedChunk = (index: number): void => {
        if (selectedIndex !== index) {
            setSelectedIndex(index);
        }
    };

    const changeFilter = (filter: AlertFilter) => {
        setFilters((prevState) =>
            prevState.map((f) =>
                f.displayIndex === filter.displayIndex ? filter : f,
            ),
        );
    };

    const updateFilters = useDebounce((newFilters: AlertFilter[]) => {
        setAlert((prevState) => ({
            ...prevState,
            filters: newFilters.map((f) => ({
                field: f.field,
                value: f.value,
                valueScale: f.valueScale,
                operation: f.operation,
                displayIndex: f.displayIndex,
                type: f.type,
            })),
        }));
    }, 500);

    useEffect(() => {
        updateFilters(filters);
    }, [filters]);

    const isDisabled = (): boolean => {
        if (chartMetadataLoading) {
            return true;
        }
        return filters.some((f) => !isAlertFilterValid(f));
    };

    return (
        <Flex direction="column">
            <Flex direction="column">
                <AlertScopeWarning
                    metricScope={metricScope}
                    dimensionScope={dimensionScope}
                    filters={filters}
                />
                <List>
                    {filters.map((filter) => (
                        <ListItem key={filter.displayIndex}>
                            <AlertFilterChunk
                                filter={filter}
                                selectedIndex={selectedIndex}
                                changeSelectedChunk={changeSelectedChunk}
                                removeChunk={removeChunk}
                                changeFilter={changeFilter}
                                userScopes={userScopes}
                                scopes={scopes}
                            />
                        </ListItem>
                    ))}
                </List>

                <Button
                    startIcon={
                        availableFiltersLoading || chartMetadataLoading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: MANTY_COLORS.mediumGrey }}
                            />
                        ) : (
                            <Add />
                        )
                    }
                    disabled={
                        !alert.metric ||
                        availableFiltersLoading ||
                        chartMetadataLoading
                    }
                    onClick={() => setShowTree(true)}
                >
                    {availableFiltersLoading || chartMetadataLoading
                        ? t('common.loading')
                        : t('alert.create.addFilter')}
                </Button>
                <Tree
                    testid="add-filter"
                    treeData={treeData}
                    isLoading={isLoading}
                    onClick={onFilterClick}
                    onClose={() => {
                        setShowTree(false);
                    }}
                    open={showTree}
                    showConfirmButton
                />
            </Flex>
            {action.name === 'create' && (
                <Flex align="center" sx={{ gap: 2 }}>
                    <PreviousStepButton />
                    <NextStepButton
                        disabled={isDisabled()}
                        className="pendo-alerts-perimeters-step"
                    />
                </Flex>
            )}
        </Flex>
    );
};

export default AlertPerimeters;
